import { hashKey, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { isApiError } from '../../api/client'
import { type QueueItemDetail, type Suggestion, type SuggestionStatus, SuggestionAccepted } from '@rp/contracts'
import { formatDateTime } from '../../lib/time'
import { Button, Card, Chip, ConfidenceMeter, Spinner, ThresholdMeter, toneClass, type StatusTone } from '../../design'
import { canRequestSuggestion } from '../../session/roles'
import { useReadySession } from '../../session/useSession'
import { queueKeys, useQueueItem } from '../queue/queries'
import { useServiceStatusClear } from '../status/useServiceStatus'
import { SUGGESTION_COPY } from './copy'
import { failureHintOf, stateLabelOf } from './failureCopy'
import { predatesLatestMessage } from './freshness'
import { GroundingList } from './GroundingList'
import { useGroundingSelection } from './useGroundingSelection'
import { useSourceText } from './useSourceText'

const AGAIN_LABELS: Partial<Record<SuggestionStatus, string>> = {
  failed: SUGGESTION_COPY.retry,
  stale: SUGGESTION_COPY.regenerate,
  no_confident: SUGGESTION_COPY.regenerate,
}

const HINTS: Partial<Record<SuggestionStatus, string>> = {
  no_confident: SUGGESTION_COPY.noConfidentHint,
  stale: SUGGESTION_COPY.staleHint,
}

const hintOf = (suggestion: Suggestion): string | undefined =>
  suggestion.status === 'failed' ? failureHintOf(suggestion) : HINTS[suggestion.status]

const STATE_TONES: Record<SuggestionStatus, StatusTone> = {
  pending: 'info',
  ready: 'success',
  no_confident: 'warning',
  failed: 'danger',
  stale: 'warning',
}

type Guidance = { hint: string | undefined; againLabel: string | undefined }

const guidanceOf = (item: QueueItemDetail, suggestion: Suggestion): Guidance =>
  predatesLatestMessage(item.messages, suggestion)
    ? { hint: SUGGESTION_COPY.outdatedHint, againLabel: SUGGESTION_COPY.regenerate }
    : { hint: hintOf(suggestion), againLabel: AGAIN_LABELS[suggestion.status] }

const adoptErrorOf = (error: unknown): string =>
  isApiError(error) && error.code === 'NOT_FOUND' ? SUGGESTION_COPY.adoptGone : SUGGESTION_COPY.adoptFailed

const GeneratedAt = ({ suggestion }: { suggestion: Suggestion }) =>
  suggestion.status === 'pending' ? null : (
    <p className="suggestion-generated num">
      {SUGGESTION_COPY.generatedAt(formatDateTime(new Date(suggestion.createdAt)))}
    </p>
  )

const wroteTheReply = (status: SuggestionStatus): boolean => status === 'ready' || status === 'pending'

const bandClass = (status: SuggestionStatus): string =>
  wroteTheReply(status) ? 'suggestion-band suggestion-band-machine' : `suggestion-band ${toneClass(STATE_TONES[status])}`

const RequestError = ({ message }: { message: string | null }) =>
  message === null ? null : (
    <p className="suggestion-error tone-danger" role="alert">
      {message}
    </p>
  )

const GateReading = ({ suggestion }: { suggestion: Suggestion }) => {
  const { status, confidence, threshold } = suggestion
  if (status !== 'no_confident' || confidence === null) return null
  if (threshold === null) return <p className="suggestion-reading num">{SUGGESTION_COPY.topSimilarity(confidence)}</p>
  return (
    <div className="suggestion-gate">
      <ThresholdMeter
        value={confidence}
        threshold={threshold}
        label={SUGGESTION_COPY.gateLabel}
        valueLabel={SUGGESTION_COPY.topSimilarity(confidence)}
        thresholdLabel={SUGGESTION_COPY.gate(threshold)}
      />
    </div>
  )
}

const SuggestionHead = ({ suggestion }: { suggestion: Suggestion }) => (
  <div className={bandClass(suggestion.status)}>
    <h2 className="suggestion-title">{SUGGESTION_COPY.title}</h2>
    <Chip tone={STATE_TONES[suggestion.status]} variant={wroteTheReply(suggestion.status) ? 'outline' : 'solid'}>
      {stateLabelOf(suggestion)}
    </Chip>
    {suggestion.confidence === null || !wroteTheReply(suggestion.status) ? null : (
      <ConfidenceMeter value={suggestion.confidence} label={SUGGESTION_COPY.confidence} />
    )}
  </div>
)

type RegenerateProps = {
  allowed: boolean
  pending: boolean
  selectedIds: readonly string[]
  onRegenerate: () => void
}

const RegenerateFromSelection = ({ allowed, pending, selectedIds, onRegenerate }: RegenerateProps) => (
  <Button
    className="suggestion-regenerate"
    disabled={!allowed || selectedIds.length === 0}
    pending={pending}
    pendingLabel={SUGGESTION_COPY.pending}
    onClick={onRegenerate}
  >
    {SUGGESTION_COPY.regenerateFromSelection}
  </Button>
)

type NothingYetProps = {
  ask: (label: string, variant?: 'primary') => ReactNode
  hint: string
  error: string | null
}

const NothingYet = ({ ask, hint, error }: NothingYetProps) => (
  <Card as="section" className="suggestion">
    <div className="suggestion-band">
      <h2 className="suggestion-title">{SUGGESTION_COPY.title}</h2>
      <Chip>{SUGGESTION_COPY.none}</Chip>
    </div>
    <div className="suggestion-body">
      <p className="suggestion-hint">{hint}</p>
      {ask(SUGGESTION_COPY.request, 'primary')}
      <RequestError message={error} />
    </div>
  </Card>
)

type SuggestionPanelProps = {
  item: QueueItemDetail
  canAdopt: boolean
  onAdopt: (text: string) => void
}

export const SuggestionPanel = ({ item, canAdopt, onAdopt }: SuggestionPanelProps) => {
  const { api, membership } = useReadySession()
  const queryClient = useQueryClient()
  const clearStatus = useServiceStatusClear()
  const suggestion = item.suggestion
  const { selectedIds, toggle } = useGroundingSelection(suggestion)
  const adoptSource = useSourceText(item.id, onAdopt)

  useQueueItem(item.id, { refetchInterval: suggestion?.status === 'pending' ? 2_000 : false })

  const request = useMutation({
    mutationFn: async (sourceIds: readonly string[] | null) => {
      await api.request(SuggestionAccepted, {
        method: 'POST',
        path: `/v1/queue/${item.id}/suggest`,
        ...(sourceIds === null ? {} : { body: { sourceIds } }),
      })
    },
    onSuccess: () => {
      clearStatus('suggestions_paused')
      const thisItem = queueKeys.detail(membership.tenantId, item.id)
      void queryClient.invalidateQueries({
        queryKey: queueKeys.all,
        predicate: (query) => query.queryHash !== hashKey(thisItem),
      })
      return queryClient.invalidateQueries({ queryKey: thisItem, exact: true })
    },
  })

  const allowed = canRequestSuggestion(membership.role)
  const requestError = isApiError(request.error) ? request.error.message : null
  const ask = (label: string, variant?: 'primary') => (
    <Button
      className="suggestion-request"
      {...(variant === undefined ? {} : { variant })}
      disabled={!allowed}
      pending={request.isPending}
      pendingLabel={SUGGESTION_COPY.pending}
      onClick={() => request.mutate(null)}
    >
      {label}
    </Button>
  )

  if (suggestion === null) {
    return <NothingYet ask={ask} hint={SUGGESTION_COPY.waitingHints[item.autoSuggestion]} error={requestError} />
  }

  const { hint, againLabel } = guidanceOf(item, suggestion)

  return (
    <Card as="section" className="suggestion">
      <SuggestionHead suggestion={suggestion} />
      <div className="suggestion-body">
        {suggestion.status === 'pending' ? <Spinner label={SUGGESTION_COPY.pending} /> : null}
        {suggestion.text === null ? null : <p className="suggestion-text">{suggestion.text}</p>}
        {hint === undefined ? null : <p className="suggestion-hint">{hint}</p>}
        <GateReading suggestion={suggestion} />
        <GroundingList
          sources={suggestion.grounding}
          selection={{
            selectedIds,
            adoptingId: adoptSource.isPending ? (adoptSource.variables ?? null) : null,
            canAdopt,
            onToggle: toggle,
            onAdopt: (sourceId) => adoptSource.mutate(sourceId),
          }}
        />
        <RequestError message={adoptSource.isError ? adoptErrorOf(adoptSource.error) : null} />
        {suggestion.grounding.length === 0 ? null : (
          <RegenerateFromSelection
            allowed={allowed}
            pending={request.isPending}
            selectedIds={selectedIds}
            onRegenerate={() => request.mutate(selectedIds)}
          />
        )}
        {suggestion.provider === null || suggestion.model === null ? null : (
          <p className="suggestion-provider num">{SUGGESTION_COPY.provider(suggestion.provider, suggestion.model)}</p>
        )}
        <GeneratedAt suggestion={suggestion} />
        {againLabel === undefined ? null : ask(againLabel)}
        <RequestError message={requestError} />
      </div>
    </Card>
  )
}
